import type { ExplorationAccountCache, ExplorationVolume } from '../types/exploration';

import { findVolumeByEbsId } from './findVolumeByEbsId';

export type UrlVolumeHydrationResult =
  | { status: 'none' }
  | { status: 'already_selected' }
  | { status: 'pending'; volumeId: string }
  | { status: 'ready'; volume: ExplorationVolume };

export interface ShouldAutoCollapseSidebarOptions {
  initialLoading: boolean;
  accountId: string | null;
  hasScopedData: boolean;
  hasSelection: boolean;
  sidebarCollapsed: boolean;
  urlVolumeId: string | null;
  selectedVolumeId: string | null;
  accounts: Record<string, ExplorationAccountCache>;
  /** When non-zero, keep sidebar visible even if scope is empty (mis-parsed drill). */
  drillPathLength?: number;
}

/** True when URL names a volume that exists locally but is not selected yet. */
export function isUrlVolumePendingSelection(
  urlVolumeId: string | null,
  selectedVolumeId: string | null,
  accounts: Record<string, ExplorationAccountCache>,
  accountId: string | null
): boolean {
  if (!urlVolumeId || selectedVolumeId === urlVolumeId) return false;
  return findVolumeByEbsId(accounts, accountId, urlVolumeId) != null;
}

/** Whether the sidebar should auto-collapse for an empty scope (post-load). */
export function shouldAutoCollapseSidebar(opts: ShouldAutoCollapseSidebarOptions): boolean {
  if (opts.initialLoading || !opts.accountId || opts.sidebarCollapsed) return false;
  if (opts.hasScopedData || opts.hasSelection) return false;
  if ((opts.drillPathLength ?? 0) > 0) return false;
  if (
    isUrlVolumePendingSelection(
      opts.urlVolumeId,
      opts.selectedVolumeId,
      opts.accounts,
      opts.accountId
    )
  ) {
    return false;
  }
  return true;
}

/** Resolve volume selection state for a URL `volume` param. */
export function resolveUrlVolumeHydration(
  accounts: Record<string, ExplorationAccountCache>,
  accountId: string | null,
  urlVolumeId: string | null,
  selectedVolumeId: string | null
): UrlVolumeHydrationResult {
  if (!urlVolumeId) return { status: 'none' };
  if (selectedVolumeId === urlVolumeId) return { status: 'already_selected' };

  const volume = findVolumeByEbsId(accounts, accountId, urlVolumeId);
  if (!volume) return { status: 'pending', volumeId: urlVolumeId };
  return { status: 'ready', volume };
}
